import commentIcon from '@iconify/icons-ic/round-comment';
import { Icon } from '@iconify/react';
import {
    Alert,
    Box,
    Card,
    Divider,
    Stack,
    styled,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    tooltipClasses,
    TooltipProps,
    Typography,
} from '@mui/material';
import convert from 'lodash/fp/convert';
import React, { useEffect, useRef, useState } from 'react';

import { IGroup, IStudentAttendance } from '@customTypes/group';
import { IUser } from '@customTypes/user';
import Scrollbar from 'components/Scrollbar/Scrollbar';
import { TableNoData } from 'components/Table';
import { getGroupStudentAttendance, getUserGroups } from 'external/firebase/firestore/collections/groups';
import useLocales from 'hooks/useLocales';
import { convertDateToString, fDate, isToday } from 'utils/formatTime';

import { AttendanceReportLegend, COLORS, ICONS } from './AttendanceReportLegend';
import CalendarToolbar from './calendar/CalendarToolbar';

const STATUS_OPTIONS = ['active', 'graduated'] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

interface AttendanceReportProps {
    selectedChild?: IUser;
}

const StudentAttendanceReport: React.FunctionComponent<AttendanceReportProps> = ({ selectedChild }) => {
    const { translate, currentLang } = useLocales();

    const [date, setDate] = useState(new Date());
    const [groups, setGroups] = useState<IGroup[]>([]);
    const [groupAttendance, setGroupAttendance] = useState<{
        [key: string]: { [key: string]: IStudentAttendance };
    }>({});
    const [error, setError] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [days, setDays] = useState<Date[]>([]);
    const [filterStatus, setFilterStatus] = useState<StatusOption>('active');

    const fetchUserGroups = async (userId: string) => {
        try {
            const userGroups = await getUserGroups(userId);
            setGroups(userGroups);
            setError(undefined);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            // @ts-ignore
            setError(translate(err?.code ?? 'unknownError'));
            setIsLoading(false);
        }
    };

    const fetchGroupStudentAttendance = async (groupId: string, userId: string) => {
        try {
            const groupStudentAttendance = await getGroupStudentAttendance(groupId, userId);
            setGroupAttendance(currentGroupAttendance => ({
                ...currentGroupAttendance,
                [groupId]: groupStudentAttendance,
            }));
            setError(undefined);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            // @ts-ignore
            setError(translate(err?.code ?? 'unknownError'));
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedChild) {
            fetchUserGroups(selectedChild.id);
        }
    }, [selectedChild]);

    useEffect(() => {
        if (selectedChild && groups.length > 0) {
            // eslint-disable-next-line no-restricted-syntax
            for (const group of groups) {
                fetchGroupStudentAttendance(group.id, selectedChild.id);
            }
        }
    }, [groups, selectedChild]);

    useEffect(() => {
        setDays(
            Array.from(
                { length: new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() },
                (_, i) => new Date(date.getFullYear(), date.getMonth(), i + 1),
            ),
        );
    }, [date]);

    const handleClickDatePrev = () => {
        setDate(new Date(date.getMonth() - 1));
        const previousMonth = new Date(date);
        previousMonth.setMonth(date.getMonth() - 1);
        setDate(previousMonth);
    };

    const handleClickDateNext = () => {
        const nextMonth = new Date(date);
        nextMonth.setMonth(date.getMonth() + 1);
        setDate(nextMonth);
    };

    const handleDateChange = date => {
        setDate(date);
    };

    const handleFilterStatus = (event: React.SyntheticEvent, newValue: StatusOption) => {
        setFilterStatus(newValue);
    };

    const filteredGroups = groups.filter(group => group.status === filterStatus);

    return (
        <Card>
            {error && <Alert severity='error'>{error}</Alert>}

            <CalendarToolbar
                date={date}
                onNextDate={handleClickDateNext}
                onPrevDate={handleClickDatePrev}
                onChangeDate={handleDateChange}
            />

            <Tabs
                value={filterStatus}
                onChange={handleFilterStatus}
                sx={{
                    px: 2,
                    bgcolor: 'background.neutral',
                }}
            >
                {STATUS_OPTIONS.map(tab => (
                    <Tab key={tab} label={translate(tab)} value={tab} />
                ))}
            </Tabs>

            <Divider />

            <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                <Scrollbar>
                    <Table size={'small'} sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell align='right'></TableCell>
                                {days.map(day => (
                                    <TableCell key={`weekday-${day.getDate()}`} align='center' sx={{ p: 0 }}>
                                        {fDate(day, currentLang.value, 'EEEEEE')}
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow>
                                <TableCell align='right'></TableCell>
                                {days.map(day => (
                                    <TableCell key={`${day.getDate()}`} align='center' sx={{ p: 0 }}>
                                        <Typography
                                            variant='subtitle2'
                                            sx={{
                                                backgroundColor: isToday(day) ? 'primary.main' : 'transparent',
                                                color: isToday(day) ? 'primary.contrastText' : 'text.primary',
                                                borderRadius: 0.25,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {day.getDate()}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredGroups.map(group => (
                                <TableRow hover key={group.id}>
                                    <TableCell align='right' component='th' scope='row' sx={{ whiteSpace: 'nowrap' }}>
                                        {group.name}
                                    </TableCell>
                                    {days.map(day => {
                                        const studentComment =
                                            groupAttendance?.[group.id]?.[convertDateToString(day)]?.comment;
                                        const groupComment =
                                            groupAttendance?.[group.id]?.[convertDateToString(day)]?.groupComment;

                                        return (
                                            <TableCell
                                                key={`${group.id}-${day.getDate()}`}
                                                align='center'
                                                sx={{ p: 0.5 }}
                                            >
                                                <Box
                                                    sx={{
                                                        background:
                                                            COLORS[
                                                                groupAttendance?.[group.id]?.[convertDateToString(day)]
                                                                    ?.attendance || 'default'
                                                            ],
                                                        borderRadius: 0.25,
                                                        width: '18px',
                                                        height: '18px',
                                                        margin: 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        position: 'relative', // Added for positioning the red dot
                                                    }}
                                                >
                                                    {studentComment || groupComment ? (
                                                        <LightTooltip
                                                            title={
                                                                <React.Fragment>
                                                                    {groupComment && (
                                                                        <>
                                                                            <Typography
                                                                                variant='subtitle2'
                                                                                color='inherit'
                                                                            >
                                                                                {translate('group_comment')}
                                                                            </Typography>
                                                                            <Typography
                                                                                color='inherit'
                                                                                variant='body2'
                                                                                sx={{ mt: 1, mb: 2 }}
                                                                            >
                                                                                {groupComment}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {studentComment && (
                                                                        <>
                                                                            <Typography
                                                                                variant='subtitle2'
                                                                                color='inherit'
                                                                            >
                                                                                {translate('student_comment')}
                                                                            </Typography>
                                                                            <Typography
                                                                                color='inherit'
                                                                                variant='body2'
                                                                                sx={{ mt: 1, mb: 2 }}
                                                                            >
                                                                                {studentComment}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                </React.Fragment>
                                                            }
                                                        >
                                                            {
                                                                ICONS[
                                                                    groupAttendance?.[group.id]?.[
                                                                        convertDateToString(day)
                                                                    ]?.attendance
                                                                ]
                                                            }
                                                        </LightTooltip>
                                                    ) : (
                                                        <>
                                                            {
                                                                ICONS[
                                                                    groupAttendance?.[group.id]?.[
                                                                        convertDateToString(day)
                                                                    ]?.attendance
                                                                ]
                                                            }
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}

                            <TableNoData
                                isNotFound={!isLoading && filteredGroups.length === 0}
                                title={translate('no_records')}
                            />

                            <TableNoData isNotFound={!selectedChild} title={translate('no_child_selected')} />
                        </TableBody>
                    </Table>
                </Scrollbar>
                <AttendanceReportLegend />
            </TableContainer>
        </Card>
    );
};

export default StudentAttendanceReport;
